import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Upload,
  X,
  GripVertical,
  Image as ImageIcon,
  Loader2,
  AlertCircle,
  Plane,
  ClipboardList,
  CreditCard,
  Plus,
  Trash2,
} from 'lucide-react';
import { useAppStore } from '../store';
import { uploadImage, validateImageFile, createImagePreview, revokeImagePreview } from '../services/storage.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Alert,
  AlertDescription,
} from '@/components/ui/alert';
import {
  ArrivalDepartureEditor,
  HouseRulesEditor,
  PaymentTermsEditor,
} from '@/components/editors';
import type { HolidayHome, StructuredPropertyContent } from '../types';

type ContentTab = 'arrival' | 'rules' | 'payment';

const getDefaultStructuredContent = (): StructuredPropertyContent => ({
  arrivalDeparture: {
    checkInTime: '16:00',
    checkOutTime: '10:00',
    keyInstructions: { en: '', de: '' },
    parkingInfo: { en: '', de: '' },
    directionsByCar: { en: '', de: '' },
    directionsByTrain: { en: '', de: '' },
    contactNumber: '',
  },
  houseRules: { groups: [] },
  paymentTerms: {
    paymentMethods: [],
    cancellationPolicy: [],
    cleaningFee: 0,
    petFee: 0,
    depositAmount: 0,
    paymentDeadlineDays: 30,
  },
});

interface ImageItem {
  id: string;
  url: string;
  file?: File;
  isUploading?: boolean;
  uploadProgress?: number;
}

const AddPropertyPage: React.FC = () => {
  const navigate = useNavigate();
  const { addHome, currentUser } = useAppStore();

  // Form state
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const season: 'warm' | 'cold' = 'warm';
  const [highSeasonWeekRate, setHighSeasonWeekRate] = useState(0);
  const [lowSeasonWeekRate, setLowSeasonWeekRate] = useState(0);
  const [cleaningFee, setCleaningFee] = useState(0);
  // Season date ranges
  const [highDateRanges, setHighDateRanges] = useState<{id:string;startDay:number;startMonth:number;endDay:number;endMonth:number}[]>([
    { id: 'hs-1', startDay: 1, startMonth: 7, endDay: 31, endMonth: 8 },
  ]);
  const [lowDateRanges, setLowDateRanges] = useState<{id:string;startDay:number;startMonth:number;endDay:number;endMonth:number}[]>([
    { id: 'ls-1', startDay: 1, startMonth: 4, endDay: 31, endMonth: 10 },
  ]);
  const [roomDescription, setRoomDescription] = useState('');
  const [totalBedrooms, setTotalBedrooms] = useState(0);
  const [totalBeds, setTotalBeds] = useState(0);
  const [totalBathrooms, setTotalBathrooms] = useState(0);
  const [maxGuests, setMaxGuests] = useState(0);
  const [structuredContent, setStructuredContent] = useState<StructuredPropertyContent>(getDefaultStructuredContent());
  const [activeContentTab, setActiveContentTab] = useState<ContentTab>('arrival');

  // Image state
  const [images, setImages] = useState<ImageItem[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cleanup previews on unmount
  useEffect(() => {
    return () => {
      images.forEach(img => {
        if (img.url.startsWith('blob:')) {
          revokeImagePreview(img.url);
        }
      });
    };
  }, []);

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files) return;

    const newImages: ImageItem[] = [];

    Array.from(files).forEach(file => {
      const validation = validateImageFile(file);
      if (validation.valid) {
        const preview = createImagePreview(file);
        newImages.push({
          id: `new-${Date.now()}-${Math.random()}`,
          url: preview,
          file,
        });
      } else {
        setError(validation.error || 'Invalid image file');
      }
    });

    if (newImages.length > 0) {
      setImages(prev => [...prev, ...newImages]);
      setError(null);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleRemoveImage = useCallback((index: number) => {
    setImages(prev => {
      const newImages = [...prev];
      const removed = newImages.splice(index, 1)[0];
      if (removed.url.startsWith('blob:')) {
        revokeImagePreview(removed.url);
      }
      return newImages;
    });
  }, []);

  // Drag and drop reordering
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleDragOverItem = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    setImages(prev => {
      const newImages = [...prev];
      const draggedItem = newImages[draggedIndex];
      newImages.splice(draggedIndex, 1);
      newImages.splice(index, 0, draggedItem);
      setDraggedIndex(index);
      return newImages;
    });
  };

  const handleSave = async () => {
    if (!name || !description) {
      setError('Bitte Name und Beschreibung ausfüllen');
      return;
    }

    if (!highSeasonWeekRate || !lowSeasonWeekRate) {
      setError('Bitte Nachtpreise für Haupt- und Nebensaison eingeben');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      // Upload images
      const uploadedUrls: string[] = [];

      for (const img of images) {
        if (img.file) {
          setImages(prev => prev.map(i =>
            i.id === img.id ? { ...i, isUploading: true } : i
          ));

          const uploadedUrl = await uploadImage(img.file, 'holiday-homes', (progress) => {
            setImages(prev => prev.map(i =>
              i.id === img.id ? { ...i, uploadProgress: progress } : i
            ));
          });
          uploadedUrls.push(uploadedUrl);

          setImages(prev => prev.map(i =>
            i.id === img.id ? { ...i, url: uploadedUrl, isUploading: false, uploadProgress: undefined, file: undefined } : i
          ));
        }
      }

      // Create home data
      const homeData: Omit<HolidayHome, 'id'> = {
        name,
        category: category || undefined,
        description,
        shortDescription: shortDescription || undefined,
        images: uploadedUrls,
        availability: [],
        pricingStructure: {
          season,
          weekdayRate: highSeasonWeekRate,
          weekendRate: highSeasonWeekRate,
          combinedRate: highSeasonWeekRate,
          seasonalConfig: {
            residenceType: 'summer' as const,
            highSeasonRanges: highDateRanges.map(r => ({
              id: r.id,
              startMonth: r.startMonth,
              startDay: r.startDay,
              endMonth: r.endMonth,
              endDay: r.endDay,
              label: { de: 'Hochsaison', en: 'High Season' },
            })),
            lowSeasonRanges: lowDateRanges.map(r => ({
              id: r.id,
              startMonth: r.startMonth,
              startDay: r.startDay,
              endMonth: r.endMonth,
              endDay: r.endDay,
              label: { de: 'Nebensaison', en: 'Low Season' },
            })),
            highSeasonRates: {
              weekdayRate: highSeasonWeekRate,
              weekendRate: highSeasonWeekRate,
              weekRate: highSeasonWeekRate,
              familyRate: highSeasonWeekRate,
              guestRate: highSeasonWeekRate,
            },
            lowSeasonRates: {
              weekdayRate: lowSeasonWeekRate,
              weekendRate: lowSeasonWeekRate,
              weekRate: lowSeasonWeekRate,
              familyRate: lowSeasonWeekRate,
              guestRate: lowSeasonWeekRate,
            },
          },
        },
        roomDescription: roomDescription || undefined,
        structuredContent: {
          ...structuredContent,
          paymentTerms: { ...structuredContent.paymentTerms, cleaningFee },
        },
        layout: totalBedrooms > 0 || totalBeds > 0 || maxGuests > 0 ? {
          totalRooms: totalBedrooms,
          totalBedrooms,
          totalBeds,
          totalBathrooms,
          maxGuests,
          rooms: [],
        } : undefined,
      };

      await addHome(homeData);

      // Navigate back to admin
      navigate('/admin');
    } catch (err) {
      console.error('Error adding property:', err);
      setError('Failed to add property. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Access check
  if (!currentUser?.isAdmin) {
    return (
      <Card>
          <CardContent className="py-16 text-center">
          <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-xl font-semibold mb-2">Kein Zugriff</h2>
          <p className="text-muted-foreground">Du benötigst Admin-Rechte, um Unterkünfte hinzuzufügen.</p>
        </CardContent>
      </Card>
    );
  }

  const isFormValid = name && description && highSeasonWeekRate > 0 && lowSeasonWeekRate > 0;

  return (
    <div className="pb-20 lg:pb-0">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Unterkunft hinzufügen</h1>
            <p className="text-muted-foreground">Neue Ferienunterkunft erfassen</p>
          </div>
        </div>
        <Button
          onClick={handleSave}
          disabled={isSaving || !isFormValid}
        >
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Wird erstellt...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Unterkunft erstellen
            </>
          )}
        </Button>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content - Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Allgemeine Informationen</CardTitle>
              <CardDescription>Die wichtigsten Angaben zur Unterkunft</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name der Unterkunft *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name der Unterkunft eingeben"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Kategorie <span className="text-muted-foreground text-xs">(z.B. Ferienhaus, Chalet, Bauernhof)</span></Label>
                <Input
                  id="category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="z.B. Ferienhaus"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="shortDescription">Kurzbeschreibung <span className="text-muted-foreground text-xs">(Kartenansicht)</span></Label>
                <Input
                  id="shortDescription"
                  value={shortDescription}
                  onChange={(e) => setShortDescription(e.target.value)}
                  placeholder="z.B. Direkt am See, 5 km vom Dorf. Grosser Garten mit Seeblick."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Beschreibung *</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Unterkunft, Ausstattung und Umgebung beschreiben..."
                  className="min-h-[120px]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="roomDescription">Zimmeraufteilung</Label>
                <Textarea
                  id="roomDescription"
                  value={roomDescription}
                  onChange={(e) => setRoomDescription(e.target.value)}
                  placeholder="z.B. 3 Schlafzimmer, 1 Badezimmer, max. 6 Personen"
                  className="min-h-[80px]"
                />
              </div>

            </CardContent>
          </Card>

          {/* Images */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="h-5 w-5" />
                Bilder
              </CardTitle>
              <CardDescription>
                Mehrere Bilder hochladen. Per Drag &amp; Drop sortieren. Erstes Bild wird zum Titelbild.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Upload Zone */}
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className={`
                  border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer
                  ${isDragOver
                    ? 'border-primary bg-primary/5'
                    : 'border-muted-foreground/25 hover:border-primary/50'
                  }
                `}
                onClick={() => document.getElementById('image-upload')?.click()}
              >
                <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                <p className="text-sm font-medium mb-1">
                  Bilder hier ablegen oder klicken zum Hochladen
                </p>
                <p className="text-xs text-muted-foreground">
                  JPEG, PNG oder WebP, max. 5 MB pro Bild
                </p>
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFileSelect(e.target.files)}
                />
              </div>

              {/* Image Grid */}
              {images.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {images.map((image, index) => (
                    <div
                      key={image.id}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragEnd={handleDragEnd}
                      onDragOver={(e) => handleDragOverItem(e, index)}
                      className={`
                        relative group rounded-lg overflow-hidden border-2 aspect-[4/3]
                        ${draggedIndex === index ? 'opacity-50 border-primary' : 'border-transparent'}
                        ${index === 0 ? 'ring-2 ring-primary ring-offset-2' : ''}
                      `}
                    >
                      <img
                        src={image.url}
                        alt={`Property image ${index + 1}`}
                        className="w-full h-full object-cover"
                      />

                      {/* Loading overlay */}
                      {image.isUploading && (
                        <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center gap-2">
                          <Loader2 className="h-6 w-6 animate-spin text-white" />
                          {image.uploadProgress != null && (
                            <>
                              <span className="text-white text-sm font-medium">{image.uploadProgress}%</span>
                              <div className="w-3/4 h-1.5 bg-white/30 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-white rounded-full transition-all duration-300"
                                  style={{ width: `${image.uploadProgress}%` }}
                                />
                              </div>
                            </>
                          )}
                        </div>
                      )}

                      {/* Controls overlay */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors">
                        <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="bg-white/90 rounded p-1 cursor-grab">
                            <GripVertical className="h-4 w-4 text-gray-600" />
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveImage(index)}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>

                      {/* Cover badge */}
                      {index === 0 && (
                        <div className="absolute bottom-2 left-2 bg-primary text-primary-foreground text-xs px-2 py-1 rounded">
                          Titelbild
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - Right Column */}
        <div className="space-y-6">
          {/* Season date ranges */}
          <Card>
            <CardHeader>
              <CardTitle>Saisondaten</CardTitle>
              <CardDescription>Zeiträume für Hoch- und Nebensaison (Tag / Monat)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5 text-sm">
              {/* Hochsaison */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="font-semibold">Hochsaison</p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setHighDateRanges(prev => [...prev, { id: `hs-${Date.now()}`, startDay: 1, startMonth: 1, endDay: 31, endMonth: 12 }])}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Zeitraum
                  </Button>
                </div>
                <div className="space-y-2">
                  {highDateRanges.map((r, i) => (
                    <div key={r.id} className="flex items-center gap-1.5 bg-muted/40 rounded-md p-2">
                      <span className="text-xs text-muted-foreground w-6 shrink-0">Von</span>
                      <Input type="number" min="1" max="31" value={r.startDay} onChange={(e) => setHighDateRanges(prev => prev.map((x, j) => j === i ? { ...x, startDay: parseInt(e.target.value) || 1 } : x))} className="w-12 text-center px-1 h-7 text-xs" />
                      <span className="text-muted-foreground text-xs">.</span>
                      <Input type="number" min="1" max="12" value={r.startMonth} onChange={(e) => setHighDateRanges(prev => prev.map((x, j) => j === i ? { ...x, startMonth: parseInt(e.target.value) || 1 } : x))} className="w-12 text-center px-1 h-7 text-xs" />
                      <span className="text-xs text-muted-foreground mx-0.5">–</span>
                      <Input type="number" min="1" max="31" value={r.endDay} onChange={(e) => setHighDateRanges(prev => prev.map((x, j) => j === i ? { ...x, endDay: parseInt(e.target.value) || 1 } : x))} className="w-12 text-center px-1 h-7 text-xs" />
                      <span className="text-muted-foreground text-xs">.</span>
                      <Input type="number" min="1" max="12" value={r.endMonth} onChange={(e) => setHighDateRanges(prev => prev.map((x, j) => j === i ? { ...x, endMonth: parseInt(e.target.value) || 1 } : x))} className="w-12 text-center px-1 h-7 text-xs" />
                      {highDateRanges.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" className="h-6 w-6 ml-auto text-destructive hover:text-destructive" onClick={() => setHighDateRanges(prev => prev.filter((_, j) => j !== i))}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Nebensaison */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="font-semibold">Nebensaison</p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setLowDateRanges(prev => [...prev, { id: `ls-${Date.now()}`, startDay: 1, startMonth: 1, endDay: 31, endMonth: 12 }])}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Zeitraum
                  </Button>
                </div>
                <div className="space-y-2">
                  {lowDateRanges.map((r, i) => (
                    <div key={r.id} className="flex items-center gap-1.5 bg-muted/40 rounded-md p-2">
                      <span className="text-xs text-muted-foreground w-6 shrink-0">Von</span>
                      <Input type="number" min="1" max="31" value={r.startDay} onChange={(e) => setLowDateRanges(prev => prev.map((x, j) => j === i ? { ...x, startDay: parseInt(e.target.value) || 1 } : x))} className="w-12 text-center px-1 h-7 text-xs" />
                      <span className="text-muted-foreground text-xs">.</span>
                      <Input type="number" min="1" max="12" value={r.startMonth} onChange={(e) => setLowDateRanges(prev => prev.map((x, j) => j === i ? { ...x, startMonth: parseInt(e.target.value) || 1 } : x))} className="w-12 text-center px-1 h-7 text-xs" />
                      <span className="text-xs text-muted-foreground mx-0.5">–</span>
                      <Input type="number" min="1" max="31" value={r.endDay} onChange={(e) => setLowDateRanges(prev => prev.map((x, j) => j === i ? { ...x, endDay: parseInt(e.target.value) || 1 } : x))} className="w-12 text-center px-1 h-7 text-xs" />
                      <span className="text-muted-foreground text-xs">.</span>
                      <Input type="number" min="1" max="12" value={r.endMonth} onChange={(e) => setLowDateRanges(prev => prev.map((x, j) => j === i ? { ...x, endMonth: parseInt(e.target.value) || 1 } : x))} className="w-12 text-center px-1 h-7 text-xs" />
                      {lowDateRanges.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" className="h-6 w-6 ml-auto text-destructive hover:text-destructive" onClick={() => setLowDateRanges(prev => prev.filter((_, j) => j !== i))}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t pt-3 text-muted-foreground text-xs">
                Mindestaufenthalt: <span className="font-medium text-foreground">3 Nächte</span>
              </div>
            </CardContent>
          </Card>

          {/* Pricing */}
          <Card>
            <CardHeader>
              <CardTitle>Preise</CardTitle>
              <CardDescription>Nachtpreise pro Saison</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="highSeasonWeekRate">Hauptsaison Nachtpreis (CHF) *</Label>
                <Input
                  id="highSeasonWeekRate"
                  type="number"
                  min="0"
                  value={highSeasonWeekRate || ''}
                  onChange={(e) => setHighSeasonWeekRate(parseInt(e.target.value) || 0)}
                  placeholder="0"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lowSeasonWeekRate">Nebensaison Nachtpreis (CHF) *</Label>
                <Input
                  id="lowSeasonWeekRate"
                  type="number"
                  min="0"
                  value={lowSeasonWeekRate || ''}
                  onChange={(e) => setLowSeasonWeekRate(parseInt(e.target.value) || 0)}
                  placeholder="0"
                />
              </div>

              <div className="border-t pt-3 space-y-2">
                <Label htmlFor="cleaningFee">Reinigungspauschale (CHF)</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">CHF</span>
                  <Input
                    id="cleaningFee"
                    type="number"
                    min="0"
                    value={cleaningFee || ''}
                    onChange={(e) => setCleaningFee(Math.round(Number(e.target.value) || 0))}
                    placeholder="0"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Room Layout */}
          <Card>
            <CardHeader>
              <CardTitle>Zimmeraufteilung</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="totalBedrooms">Schlafzimmer</Label>
                  <Input id="totalBedrooms" type="number" min="0" value={totalBedrooms || ''} onChange={(e) => setTotalBedrooms(parseInt(e.target.value) || 0)} placeholder="0" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="totalBeds">Betten</Label>
                  <Input id="totalBeds" type="number" min="0" value={totalBeds || ''} onChange={(e) => setTotalBeds(parseInt(e.target.value) || 0)} placeholder="0" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="totalBathrooms">Badezimmer</Label>
                  <Input id="totalBathrooms" type="number" min="0" value={totalBathrooms || ''} onChange={(e) => setTotalBathrooms(parseInt(e.target.value) || 0)} placeholder="0" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="maxGuests">Max. Gäste</Label>
                  <Input id="maxGuests" type="number" min="0" value={maxGuests || ''} onChange={(e) => setMaxGuests(parseInt(e.target.value) || 0)} placeholder="0" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Content Editors */}
      <div className="mt-6">
        <Card>
          <CardHeader>
            <CardTitle>Gästeinformationen</CardTitle>
            <CardDescription>Anreise, Hausregeln und Zahlungsbedingungen</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeContentTab} onValueChange={(v) => setActiveContentTab(v as ContentTab)}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="arrival" className="gap-2">
                  <Plane className="h-4 w-4" />
                  <span className="hidden sm:inline">Anreise & Abreise</span>
                  <span className="sm:hidden">Anreise</span>
                </TabsTrigger>
                <TabsTrigger value="rules" className="gap-2">
                  <ClipboardList className="h-4 w-4" />
                  <span className="hidden sm:inline">Hausregeln</span>
                  <span className="sm:hidden">Regeln</span>
                </TabsTrigger>
                <TabsTrigger value="payment" className="gap-2">
                  <CreditCard className="h-4 w-4" />
                  <span className="hidden sm:inline">Zahlungsbedingungen</span>
                  <span className="sm:hidden">Zahlung</span>
                </TabsTrigger>
              </TabsList>
              <TabsContent value="arrival" className="mt-4">
                <ArrivalDepartureEditor
                  value={structuredContent.arrivalDeparture}
                  onChange={(v) => setStructuredContent(prev => ({ ...prev, arrivalDeparture: v }))}
                />
              </TabsContent>
              <TabsContent value="rules" className="mt-4">
                <HouseRulesEditor
                  value={structuredContent.houseRules}
                  onChange={(v) => setStructuredContent(prev => ({ ...prev, houseRules: v }))}
                />
              </TabsContent>
              <TabsContent value="payment" className="mt-4">
                <PaymentTermsEditor
                  value={structuredContent.paymentTerms}
                  onChange={(v) => setStructuredContent(prev => ({ ...prev, paymentTerms: v }))}
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Mobile Save Button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t lg:hidden">
        <Button
          onClick={handleSave}
          disabled={isSaving || !isFormValid}
          className="w-full"
        >
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Wird erstellt...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Unterkunft erstellen
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default AddPropertyPage;
